import { useEffect, useState } from "react"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { Input, Select } from "antd"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { P2, H6, Button as CustomButton } from "components"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  IShiftPlan,
  IColleaguesRes,
  IShiftAssignment,
  IShiftPlanRes,
  IShiftSetupRes,
} from "../interface/shiftData"
import { ewaAxiosInstance } from "utils/request"
import { toKeyDate } from "../utils"
import SelectColleague from "./SelectColleague"
import { onQueryError } from "utils/utils"
import { useGet } from "utils/useFetch"
import { LoadingOutlined } from "@ant-design/icons"
import DateInput, { DateInputValue } from "./DateInput"
import { isCompleteDate } from "pages/dashboard/documents/time-and-attendance/utils/shiftTablesUtils"

type ShiftSwapModalProps = {
  shiftPlan: IShiftPlan | undefined
  setShowSwapShiftModal: React.Dispatch<React.SetStateAction<boolean>>
  assignedMap: Set<string>
  shiftSetupData: IShiftSetupRes | undefined
  open: boolean
  selectedShiftAssignment: {
    assignmentId: number | undefined
    shiftPlanId: number | undefined
    shiftPeriodId: number | undefined
    shiftPeriodTitle: string | undefined
    date: string | null | undefined
  } | null
}

const SUCCESS_IMAGE =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/97f6370c-2264-422d-8bf1-fb0780b81a69.png"

new Image().src = SUCCESS_IMAGE

const ShiftSwapModal = ({
  setShowSwapShiftModal,
  shiftSetupData,
  shiftPlan,
  open,
  selectedShiftAssignment,
  assignedMap,
}: ShiftSwapModalProps) => {
  const [current, setCurrent] = useState(0)

  const [collectShift, setCollectShift] = useState("yes")
  const [selectedColleague, setSelectedColleague] = useState("")
  const [reason, setReason] = useState("")
  const formattedDate = toKeyDate(
    new Date(selectedShiftAssignment?.date ?? new Date())
  )
  const [givingDateVal, setGivingDateVal] = useState<DateInputValue>({
    dd: "",
    mm: "",
    yyyy: "",
  })

  useEffect(() => {
    const [y, m, d] = formattedDate.split("-")
    setGivingDateVal({ dd: d, mm: m, yyyy: y })
  }, [selectedShiftAssignment?.date, open])

  const [selectedSwapPlanId, setSelectedSwapPlanId] = useState("")
  const [selectedSwapPeriod, setSelectedSwapPeriod] = useState("")
  const [swapDateVal, setSwapDateVal] = useState<DateInputValue>({
    dd: "",
    mm: "",
    yyyy: "",
  })
  const [colleagueSearch, setColleagueSearch] = useState("")

  const onClose = () => {
    setShowSwapShiftModal(false)
    setCurrent(0)
    setReason("")
    setSelectedColleague("")
    setCollectShift("yes")
    setGivingDateVal({ dd: "", mm: "", yyyy: "" })
    setSelectedSwapPlanId("")
    setSelectedSwapPeriod("")
    setSwapDateVal({ dd: "", mm: "", yyyy: "" })
    setColleagueSearch("")
  }

  const { data: shiftPlanData, isLoading: isLoadingShiftPlanData } =
    useGet<IShiftPlanRes>({
      url: `/v1/time-attendance/shift-plans/${selectedSwapPlanId}`,
      instanceKey: "ewaAxiosInstance",
      enabled: !!selectedSwapPlanId,
    })

  const shiftPlans = shiftSetupData?.data?.shiftPlans
  const shiftPeriods = shiftPlanData?.data?.shiftPeriods
  const workplaceId =
    shiftSetupData?.data?.location?.workplaceId ||
    shiftSetupData?.data?.location?.id
  const requireAdminApproval = shiftPlan?.requireApprovalForSwap

  const { data: colleaguesData, isLoading: isLoadingColleaguesData } =
    useGet<IColleaguesRes>({
      url: workplaceId
        ? `/v1/time-attendance/shift-swaps/colleagues?workplaceId=${workplaceId}&limit=100`
        : "",
      instanceKey: "ewaAxiosInstance",
      queryKey: [
        `/v1/time-attendance/shift-swaps/colleagues`,
        String(workplaceId ?? ""),
      ],
      enabled: !!workplaceId,
    })

  const colleagues =
    colleaguesData?.data?.docs
      ?.filter((c) => c.firstName || c.lastName)
      .map((c) => ({
        value: String(c.id),
        label: [c.firstName, c.lastName].filter(Boolean).join(" "),
        initials: [c.firstName?.[0], c.lastName?.[0]]
          .filter(Boolean)
          .join("")
          .toUpperCase(),
      })) ?? []

  const selectedColleagueData = colleaguesData?.data?.docs?.find(
    (c) => String(c.id) === selectedColleague
  )

  const selectedColleagueName = [
    selectedColleagueData?.firstName,
    selectedColleagueData?.lastName,
  ]
    .filter(Boolean)
    .join(" ")

  const givingDate = `${givingDateVal.yyyy}-${givingDateVal.mm}-${givingDateVal.dd}`
  const isAssignedOnDate = assignedMap.has(
    `${givingDate}-${selectedShiftAssignment?.shiftPeriodId}`
  )

  const collectingDate = isCompleteDate(swapDateVal)
    ? `${swapDateVal.yyyy}-${swapDateVal.mm}-${swapDateVal.dd}`
    : null

  const checkDate = collectShift === "yes" ? collectingDate : givingDate

  const checkColleagueAssignment =
    !!selectedColleague &&
    (collectShift === "yes"
      ? !!selectedSwapPeriod && !!collectingDate
      : givingDate.length === 10)

  const { data: colleagueAssignmentData, isFetching: isCheckingColleague } =
    useQuery<{ data: IShiftAssignment[] }>({
      queryKey: [
        "/v1/time-attendance/shift-swaps/check-colleague-assignment",
        selectedColleague,
        checkDate,
      ],
      queryFn: async () =>
        (
          await ewaAxiosInstance.get(
            `/v1/time-attendance/shift-swaps/check-colleague-assignment`,
            {
              params: {
                date: checkDate,
                colleagueEmployeeId: selectedColleague,
              },
            }
          )
        ).data,
      enabled: checkColleagueAssignment && checkDate?.length === 10,
      keepPreviousData: false,
    })

  const pickedDayOfWeek = collectingDate
    ? new Date(
        Number(swapDateVal.yyyy),
        Number(swapDateVal.mm) - 1,
        Number(swapDateVal.dd)
      )
        .toLocaleDateString("en-US", { weekday: "long" })
        .toLowerCase()
    : null

  const colleagueAssignment = colleagueAssignmentData?.data?.find((a) => {
    if (String(a.shiftPeriodId) !== String(selectedSwapPeriod)) return false

    if (a.date) {
      return a.date.startsWith(collectingDate!)
    }

    if (a.dayOfWeek !== pickedDayOfWeek) return false

    if (!a.shiftTable) return false
    const picked = new Date(collectingDate!)
    const start = new Date(a.shiftTable.startDate)
    const end = new Date(a.shiftTable.endDate)
    return picked >= start && picked <= end
  })

  const isColleagueAssigned: boolean | null = !checkColleagueAssignment
    ? null
    : isCheckingColleague
    ? null
    : colleagueAssignment != null

  const colleagueHasAnyShift: boolean | null =
    collectShift !== "no" || isCheckingColleague
      ? null
      : (colleagueAssignmentData?.data?.length ?? 0) > 0

  const queryClient = useQueryClient()

  const { mutate: submitSwap, isLoading: isSubmitting } = useMutation({
    mutationFn: async (body: {
      sourceShiftAssignmentId: number | undefined
      sourceSwapDate: string
      targetShiftAssignmentId?: number | undefined
      targetSwapDate?: string
      targetEmployeeId: number | undefined
      reason: string
    }) => {
      return (
        await ewaAxiosInstance.post("/v1/time-attendance/shift-swaps", body)
      ).data
    },
    onSuccess: (data: any) => {
      if (data.success) {
        setCurrent(2)
        queryClient.invalidateQueries({
          queryKey: [
            "/v1/time-attendance/shift-swaps/requests/requested-by-employee",
          ],
        })
      }
    },
    onError: onQueryError,
  })

  const submitRequest = () => {
    if (!colleagueAssignment?.id) {
      submitSwap({
        sourceShiftAssignmentId: selectedShiftAssignment?.assignmentId,
        sourceSwapDate: givingDate,
        targetEmployeeId: selectedColleagueData?.id,
        reason: reason || "",
      })
    } else {
      submitSwap({
        sourceShiftAssignmentId: selectedShiftAssignment?.assignmentId,
        sourceSwapDate: givingDate,
        targetSwapDate: collectingDate ?? undefined,
        targetShiftAssignmentId: colleagueAssignment?.id,
        targetEmployeeId: selectedColleagueData?.id,
        reason: reason || "",
      })
    }
  }

  const stepsContent = [
    // Step 0
    <div key={0}>
      <H6 className="font-semibold">Your shift details</H6>
      <p className="text-sm my-1">
        Requesting a swap for{" "}
        <strong>{selectedShiftAssignment?.shiftPeriodTitle}</strong> in{" "}
        <strong>{shiftPlan?.title}</strong>
      </p>

      <p className="font-medium text-sm my-1">Day you're giving</p>
      <DateInput
        value={givingDateVal}
        onChange={setGivingDateVal}
        className="my-1"
      />

      {isAssignedOnDate ? (
        <p className="text-[#008236] text-sm">
          {`Confirmed, you have a shift assignment on ${givingDate
            .split("-")
            .reverse()
            .join("-")}`}
        </p>
      ) : (
        <p className="text-[#E7000B] text-sm">
          {`You do no thave a shift assignment on ${givingDate
            .split("-")
            .reverse()
            .join("-")}`}
        </p>
      )}

      <div className="flex items-center justify-between mt-2">
        <P2 className="font-semibold my-1">Colleague to swap with</P2>
      </div>
      <SelectColleague
        colleagues={colleagues}
        value={selectedColleague}
        onChange={(val) => {
          setSelectedColleague(val)
        }}
        search={colleagueSearch}
        onSearch={setColleagueSearch}
        isLoadingColleaguesData={isLoadingColleaguesData}
      />

      <P2 className="font-semibold my-1">Reason [optional]</P2>
      <Input.TextArea
        placeholder="Enter reasons for swapping this shift"
        maxLength={150}
        onChange={(e) => setReason(e.target.value)}
        style={{ width: "100%", height: "80px", resize: "none" }}
        value={reason}
        className="font-avenir mb-1"
      />
      <p className="text-slate-400 text-sm">
        Note: You can't edit your reason after sending the request
      </p>
    </div>,

    // Step 1 – Colleague's shift
    <div key={1}>
      <p className="flex items-center gap-3 mb-3">
        Collect a shift from this colleague too
        <span className="bg-white border border-slate-300 rounded-md grid grid-cols-2 items-center gap-2 w-20 justify-between">
          <button
            className={`${
              collectShift === "yes" ? "bg-slate-200" : ""
            } w-full py-0.5`}
            onClick={() => setCollectShift("yes")}
          >
            Yes
          </button>
          <button
            className={`${
              collectShift === "no" ? "bg-slate-200" : ""
            } w-full py-0.5`}
            onClick={() => {
              setCollectShift("no")
              setSelectedSwapPlanId("")
              setSelectedSwapPeriod("")
              setSwapDateVal({ dd: "", mm: "", yyyy: "" })
            }}
          >
            No
          </button>
        </span>
      </p>
      {collectShift === "no" && selectedColleague && (
        <div className="mt-2">
          {isCheckingColleague ? (
            <LoadingOutlined
              spin
              className="w-4 h-4"
            />
          ) : colleagueHasAnyShift === null ? null : colleagueHasAnyShift ? (
            <span className="flex items-center gap-1">
              <ExclamationCircleOutlined className="text-[#E7000B]" />
              <p className="text-[#E7000B] text-sm">
                {`Colleague has a clashing shift on ${givingDate
                  .split("-")
                  .reverse()
                  .join("-")}`}
              </p>
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <ExclamationCircleOutlined className="text-[#008236]" />
              <p className="text-[#008236] text-sm">
                {`Colleague doesn't have a clashing shift on ${givingDate
                  .split("-")
                  .reverse()
                  .join("-")}`}
              </p>
            </span>
          )}
        </div>
      )}
      <div className={collectShift === "no" ? "hidden" : "block"}>
        <H6 className="font-semibold">Colleague's shift details</H6>
        <p className="flex items-center gap-2 text-sm my-2">
          Viewing shifts for:{" "}
          <span className="bg-slate-100 border border-slate-200 rounded-full w-fit px-2 py-1">
            {selectedColleagueName}
          </span>
        </p>

        <P2 className="font-semibold my-2">Shift plan</P2>
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          placeholder="Search for a shift plan"
          value={selectedSwapPlanId || undefined}
          onChange={(val) => {
            setSelectedSwapPlanId(val)
            setSelectedSwapPeriod("")
          }}
          options={
            shiftPlans?.map((p) => ({
              value: String(p.id),
              label: p.title,
            })) ?? []
          }
        />

        <P2 className="font-semibold my-2">Shift period</P2>
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          placeholder="Search for a shift period"
          className="mb-2"
          value={selectedSwapPeriod || undefined}
          onChange={(val) => {
            setSelectedSwapPeriod(val)
          }}
          options={
            shiftPeriods?.map((p) => ({
              value: String(p.id),
              label: p.title,
            })) ?? []
          }
          loading={isLoadingShiftPlanData}
        />

        <p className="text-sm font-semibold mb-1">Day you're collecting</p>
        <DateInput
          value={swapDateVal}
          onChange={setSwapDateVal}
          className="my-1"
        />

        {isCheckingColleague ? (
          <LoadingOutlined
            spin
            className="w-4 h-4 "
          />
        ) : isColleagueAssigned === null ? null : isColleagueAssigned ? (
          <p className="text-[#008236] text-sm">
            {`Confirmed, colleague has a shift assignment on ${collectingDate
              ?.split("-")
              .reverse()
              .join("-")}`}
          </p>
        ) : (
          <p className="text-[#E7000B] text-sm">
            {`This colleague doesn't have a shift on ${collectingDate
              ?.split("-")
              .reverse()
              .join("-")}`}
          </p>
        )}
      </div>
    </div>,

    // Step 2 – Success
    <div
      key={2}
      className="flex flex-col justify-center items-center h-56"
    >
      <img
        src={SUCCESS_IMAGE}
        width={120}
        height={121.27}
        alt="success"
      />
      <P2 className="font-semibold mt-2">Swap request sent</P2>
      <p className="text-sm text-slate-400 text-center mt-1">
        {requireAdminApproval
          ? "Pending colleague and admin approval"
          : "Pending colleague approval"}
      </p>
    </div>,
  ]
  const canSubmit = collectShift === "no" ? true : !!isColleagueAssigned

  const next = () =>
    setCurrent((prev) => Math.min(prev + 1, stepsContent.length - 1))

  const progressPercent = ((current + 1) / stepsContent.length) * 100

  return (
    <CustomModal
      destroyOnClose
      width={500}
      open={open}
      title="Create shift swap request"
      onCancel={onClose}
      centered
    >
      <div className="w-full flex items-center gap-2">
        {stepsContent.map((_, i) => {
          const segmentPercent = 100 / stepsContent.length
          const filled = Math.max(
            0,
            Math.min(
              ((progressPercent - i * segmentPercent) / segmentPercent) * 100,
              100
            )
          )
          return (
            <div
              key={i}
              className="flex-1 h-1 bg-gray-200 rounded-md overflow-hidden relative"
            >
              <div
                className="absolute left-0 top-0 h-2 bg-blue-600 transition-all duration-300"
                style={{ width: `${filled}%` }}
              />
            </div>
          )
        })}
      </div>

      <div className="my-4">{stepsContent[current]}</div>

      <div className="flex gap-3 items-center">
        {current === 0 && (
          <CustomButton
            color="primary"
            style={{ width: "100%" }}
            disabled={!selectedColleague}
            onClick={() => {
              next()
            }}
          >
            Next
          </CustomButton>
        )}

        {current === 1 && (
          <CustomButton
            color="primary"
            style={{ width: "100%" }}
            onClick={() => submitRequest()}
            submitting={isSubmitting}
            disabled={
              !canSubmit ||
              isSubmitting ||
              !!colleagueHasAnyShift ||
              isCheckingColleague
            }
          >
            Submit request
          </CustomButton>
        )}
      </div>
    </CustomModal>
  )
}

export default ShiftSwapModal
